import { Component, OnInit, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { ConfirmationService, MessageService, PrimeIcons } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { lastValueFrom, map } from 'rxjs';
import {
  AdminControllerService,
  GCPDatabaseUserResponse,
  GcpDatabaseUserControllerService,
  UserRoleViewResponse
} from 'src/app/admin-api';
import {
  DropdownFilter,
  TableActionButton,
  TableColumn
} from 'src/app/components/table';
import { getAllRoles, getRole } from 'src/app/models';
import { StorageService } from 'src/app/services/auth/storage.service';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import { FormUtil } from 'src/app/utils/form.util';
import { DbUserCreateModalComponent } from './database-users/db-user-create-modal/db-user-create-modal.component';
import { DbUserPasswordModalComponent } from './database-users/db-user-password-modal/db-user-password-modal.component';
import { DbUserPermissionsModalComponent } from './database-users/db-user-permissions-modal/db-user-permissions-modal.component';
import { PasswordUpdateModalComponent } from './password-update-modal/password-update-modal.component';
import { UserFormModalComponent } from './user-form-modal/user-form-modal.component';

interface SystemUser extends UserRoleViewResponse {
  role: string;
  hasDatabase?: boolean;
  dbUsername?: string;
  isProtected?: boolean;
  isExternal?: boolean;
}

@Component({
  selector: 'app-system-users',
  templateUrl: './system-users.component.html',
  styleUrls: ['./system-users.component.scss'],
  providers: [ConfirmationService, DialogService, MessageService]
})
export class SystemUsersComponent implements OnInit {
  private adminService = inject(AdminControllerService);
  private dbUserService = inject(GcpDatabaseUserControllerService);
  private confirmationService = inject(ConfirmationService);
  private messageService = inject(MessageService);
  private dialog = inject(DialogService);
  private title = inject(Title);
  private router: Router = inject(Router);

  allowedUsers: Array<SystemUser> | undefined;
  externalDbUsers: Array<GCPDatabaseUserResponse> | undefined;
  externalCols: Array<TableColumn> = [
    new TableColumn(
      'Nome do Usuário',
      'displayName',
      true,
      'text',
      undefined,
      undefined,
      true,
      'contains'
    ),
    new TableColumn('Ação', '', false, 'button')
  ];
  externalActionButtons: Array<TableActionButton>;
  permissions = [
    { label: 'Usuário', value: 'ROLE_ALLOC_USER' },
    { label: 'Admin', value: 'ROLE_ALLOC_ADMIN' }
  ];
  allPermissions: Array<{ label: string; value: string }>;
  cols: Array<TableColumn> = [
    new TableColumn('Id', 'userId', true, 'number'),
    new TableColumn(
      'Nome',
      'displayName',
      true,
      'text',
      undefined,
      undefined,
      true,
      'contains'
    ),
    new TableColumn(
      'Email',
      'username',
      true,
      'text',
      undefined,
      undefined,
      true,
      'startsWith'
    ),
    new TableColumn(
      'Permissão',
      'role',
      true,
      'text',
      undefined,
      undefined,
      true,
      'in'
    ),
    new TableColumn(
      'Usuário Glam',
      'personId',
      true,
      'number',
      '/users/person/',
      'personId',
      true,
      'equals'
    ),
    new TableColumn(
      'Login DB',
      'dbUsername',
      true,
      'text',
      undefined,
      undefined,
      true,
      'contains'
    ),
    new TableColumn('Ação', '', false, 'button')
  ];
  actionButtons: Array<TableActionButton>;
  dropdownFilters: { [key: string]: Array<DropdownFilter> } = {
    role: [{ label: 'Todas', value: '' }].concat(
      getAllRoles().map((r) => ({
        label: r.label,
        value: r.label
      }))
    )
  };

  async ngOnInit(): Promise<void> {
    LoaderService.showLoader();
    this.title.setTitle('Usuários do sistema');
    this.actionButtons = [
      new TableActionButton(
        '',
        'changePassword',
        PrimeIcons.KEY,
        () => true,
        '',
        'Alterar senha',
        'bottom',
        true,
        true,
        'warning'
      ),
      new TableActionButton(
        '',
        'editUser',
        PrimeIcons.PENCIL,
        () => true,
        '',
        'Editar usuário',
        'bottom',
        true,
        true,
        'primary'
      ),
      new TableActionButton(
        '',
        'excludeUser',
        PrimeIcons.TRASH,
        (user: SystemUser) => StorageService.userId !== user.userId,
        '',
        'Excluir usuário',
        'bottom',
        true,
        true,
        'danger'
      ),
      new TableActionButton(
        '',
        'createDbLogin',
        PrimeIcons.DATABASE,
        (user: SystemUser) =>
          !user.isProtected && !user.isExternal && !user.hasDatabase,
        '',
        'Criar login de banco',
        'bottom',
        true,
        true,
        'success'
      ),
      new TableActionButton(
        '',
        'editDbPermissions',
        PrimeIcons.SHIELD,
        (user: SystemUser) =>
          !user.isProtected && !user.isExternal && !!user.hasDatabase,
        '',
        'Permissões de banco',
        'bottom',
        true,
        true,
        'info'
      ),
      new TableActionButton(
        '',
        'changeDbPassword',
        PrimeIcons.LOCK,
        (user: SystemUser) =>
          !user.isProtected && !user.isExternal && !!user.hasDatabase,
        '',
        'Alterar senha do banco',
        'bottom',
        true,
        true,
        'warning'
      ),
      new TableActionButton(
        '',
        'removeDbLogin',
        PrimeIcons.DATABASE,
        (user: SystemUser) =>
          !user.isProtected && !user.isExternal && !!user.hasDatabase,
        '',
        'Remover login de banco',
        'bottom',
        true,
        true,
        'danger'
      )
    ];
    this.externalActionButtons = [
      new TableActionButton(
        '',
        'removeExternalUser',
        PrimeIcons.TRASH,
        (user: GCPDatabaseUserResponse) => !!user.userId,
        '',
        'Remover usuário do banco',
        'bottom',
        true,
        true,
        'danger'
      )
    ];
    await this.findUsers();
    LoaderService.showLoader(false);
  }

  async findUsers(): Promise<void> {
    try {
      const [adminUsers, dbUsers] = await Promise.all([
        lastValueFrom(
          this.adminService.getAllowedUsers().pipe(map((data) => data.result))
        ),
        this.loadDbUsers()
      ]);

      const dbMap = new Map<number, GCPDatabaseUserResponse>();
      (dbUsers || []).forEach((db) => {
        if (db.userId) dbMap.set(db.userId, db);
      });

      this.allowedUsers = (adminUsers || []).map((u) => {
        const db = dbMap.get(u.userId as number);
        return {
          ...u,
          role: getRole(FormUtil.getRolePrincipal(u.roles as string))
            ?.label as string,
          hasDatabase: db?.hasDatabase ?? false,
          dbUsername: db?.hasDatabase ? (db?.dbUsername ?? '') : '',
          isProtected: db?.isProtected ?? false,
          isExternal: false
        };
      });

      this.externalDbUsers = (dbUsers || []).filter((db) => db.hasDatabase);
    } catch (error) {
      AppDialogService.showErrorDialog(error);
      this.allowedUsers = [];
      this.externalDbUsers = [];
    }
  }

  private async loadDbUsers(): Promise<GCPDatabaseUserResponse[]> {
    try {
      return await lastValueFrom(
        this.dbUserService
          .listDatabaseUsers()
          .pipe(map((data) => data.result as GCPDatabaseUserResponse[]))
      );
    } catch {
      return [];
    }
  }

  callAction($event: {
    item: SystemUser;
    $event: Event;
    action: string;
  }): void {
    const actions: Record<string, (user: SystemUser, event: Event) => void> = {
      changePassword: (u) => this.changePassword(u),
      editUser: (u) => this.editUser(u),
      excludeUser: (u, e) => this.excludeUser(u, e),
      createDbLogin: (u) => this.createDbLogin(u),
      editDbPermissions: (u) => this.editDbPermissions(u),
      changeDbPassword: (u) => this.changeDbPassword(u),
      removeDbLogin: (u, e) => this.removeDbLogin(u, e)
    };
    actions[$event.action]?.($event.item, $event.$event);
  }

  addNewUser(): void {
    LoaderService.showLoader(false);
    this.dialog
      .open(UserFormModalComponent, {
        showHeader: true,
        header: 'Novo usuário',
        dismissableMask: true,
        closable: false
      })
      .onClose.subscribe(async (success: boolean) => {
        if (success) {
          LoaderService.showLoader();
          delete this.allowedUsers;
          await this.findUsers();
          LoaderService.showLoader(false);
        }
      });
  }

  editUser(user: SystemUser): void {
    this.dialog
      .open(UserFormModalComponent, {
        showHeader: true,
        closable: false,
        header: 'Editar usuário',
        dismissableMask: false,
        data: {
          user
        }
      })
      .onClose.subscribe(async (success: boolean) => {
        if (success) {
          LoaderService.showLoader();
          delete this.allowedUsers;
          await this.findUsers();
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Usuário atualizado com sucesso'
          });

          LoaderService.showLoader(false);
        }
      });
  }

  excludeUser(user: UserRoleViewResponse, event: Event): void {
    this.confirmationService.confirm({
      target: event.target as HTMLButtonElement,
      message: `Deseja remover o acesso do usuário ${user.username}?`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sim',
      rejectLabel: 'Não',
      acceptButtonStyleClass: 'p-button-danger',
      accept: async () => {
        LoaderService.showLoader();
        try {
          await lastValueFrom(
            this.adminService.deleteUser(user.userId as number)
          );
          delete this.allowedUsers;
          await this.findUsers();
          this.messageService.add({
            severity: 'info',
            summary: 'Sucesso',
            detail: 'Usuário removido com sucesso'
          });
        } catch (error) {
          AppDialogService.showErrorDialog(error);
        }
        LoaderService.showLoader(false);
      }
    });
  }

  getRoleLabel(role: string): string {
    return getRole(role)?.label || 'NONE';
  }

  changePassword(user: SystemUser): void {
    if (user.userId === StorageService.userId) {
      this.router.navigate(['/settings/account']);
      return;
    }
    this.dialog
      .open(PasswordUpdateModalComponent, {
        showHeader: true,
        header: 'Alterar senha',
        dismissableMask: false,
        width: '500px',
        data: {
          user
        }
      })
      .onClose.subscribe(async (success: boolean) => {
        if (success) {
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Senha atualizada com sucesso'
          });
        }
      });
  }

  createDbLogin(user: SystemUser): void {
    this.dialog
      .open(DbUserCreateModalComponent, {
        showHeader: true,
        header: `Criar login de banco - ${user.displayName}`,
        dismissableMask: false,
        closable: true,
        width: '500px',
        data: { user }
      })
      .onClose.subscribe(async (success: boolean) => {
        if (success) {
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Login de banco criado com sucesso'
          });
          LoaderService.showLoader();
          delete this.allowedUsers;
          await this.findUsers();
          LoaderService.showLoader(false);
        }
      });
  }

  editDbPermissions(user: SystemUser): void {
    this.dialog
      .open(DbUserPermissionsModalComponent, {
        showHeader: true,
        header: `Permissões - ${user.displayName}`,
        dismissableMask: false,
        closable: true,
        width: '500px',
        data: { user }
      })
      .onClose.subscribe(async (success: boolean) => {
        if (success) {
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Permissões atualizadas com sucesso'
          });
          LoaderService.showLoader();
          delete this.allowedUsers;
          await this.findUsers();
          LoaderService.showLoader(false);
        }
      });
  }

  changeDbPassword(user: SystemUser): void {
    this.dialog
      .open(DbUserPasswordModalComponent, {
        showHeader: true,
        header: `Alterar senha de banco - ${user.displayName}`,
        dismissableMask: false,
        closable: true,
        width: '500px',
        data: { user }
      })
      .onClose.subscribe(async (success: boolean) => {
        if (success) {
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Senha de banco alterada com sucesso'
          });
        }
      });
  }

  removeDbLogin(user: SystemUser, event: Event): void {
    this.confirmationService.confirm({
      target: event.target as HTMLButtonElement,
      message: `Tem certeza que deseja remover o login de banco de ${user.displayName}?`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sim',
      rejectLabel: 'Não',
      acceptButtonStyleClass: 'p-button-danger',
      accept: async () => {
        LoaderService.showLoader();
        try {
          user.isExternal
            ? await lastValueFrom(
                this.dbUserService.deleteExternalUser(user.dbUsername as string)
              )
            : await lastValueFrom(
                this.dbUserService.deleteDatabaseUser(user.userId as number)
              );
          this.messageService.add({
            severity: 'info',
            summary: 'Sucesso',
            detail: 'Login de banco removido com sucesso'
          });
          delete this.allowedUsers;
          await this.findUsers();
        } catch (error) {
          AppDialogService.showErrorDialog(error);
        }
        LoaderService.showLoader(false);
      }
    });
  }

  callExternalAction($event: {
    item: GCPDatabaseUserResponse;
    $event: Event;
    action: string;
  }): void {
    const actions: Record<
      string,
      (user: GCPDatabaseUserResponse, event: Event) => void
    > = {
      removeExternalUser: (u, e) => this.removeExternalUser(u, e)
    };
    actions[$event.action]?.($event.item, $event.$event);
  }

  removeExternalUser(user: GCPDatabaseUserResponse, event: Event): void {
    this.confirmationService.confirm({
      target: event.target as HTMLButtonElement,
      message: `Tem certeza que deseja remover o usuário externo ${user.displayName}?`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sim',
      rejectLabel: 'Não',
      acceptButtonStyleClass: 'p-button-danger',
      accept: async () => {
        LoaderService.showLoader();
        try {
          user.userId
            ? await lastValueFrom(
                this.dbUserService.deleteDatabaseUser(user.userId as number)
              )
            : await lastValueFrom(
                this.dbUserService.deleteExternalUser(user.dbUsername as string)
              );
          this.messageService.add({
            severity: 'info',
            summary: 'Sucesso',
            detail: 'Usuário removido do banco com sucesso'
          });
          delete this.allowedUsers;
          await this.findUsers();
        } catch (error) {
          AppDialogService.showErrorDialog(error);
        }
        LoaderService.showLoader(false);
      }
    });
  }
}
